import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CONTACT, waLink } from "@/lib/contact";

const Section = ({ title, children }) => (
  <div className="glass-card rounded-2xl p-7">
    <h2 className="font-display text-2xl text-gold-200">{title}</h2>
    <div className="mt-3 text-sm text-foreground/70 leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function InsurancePolicy() {
  useEffect(() => {
    document.title = "Insurance & Damage Policy | PB-Lux Vehicle Rental";
  }, []);
  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-28 sm:pt-32 pb-16 noise" data-testid="insurance-policy-page">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-100 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <p className="kicker">Rental Information</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3">Insurance & Damage Policy</h1>
          <p className="mt-4 text-sm text-foreground/60 max-w-xl">
            This page is for your information — it is not part of the reservation form. Please read it before your trip so you know exactly how coverage works.
          </p>

          <div className="mt-10 space-y-5">
            <Section title="Vehicle Insurance Coverage">
              <p>
                PB-Lux rental vehicles are covered by comprehensive insurance, subject to the applicable policy terms, conditions,
                exclusions, and insurer approval. There is <span className="text-gold-200">no separate daily insurance fee</span> —
                coverage is included with your rental.
              </p>
            </Section>

            <Section title="Insurance Claim Participation Fee">
              <p>If an incident leads to an applicable insurance claim, a one-time participation fee applies:</p>
              <ul className="list-disc list-inside marker:text-gold-500 space-y-1.5">
                <li>Small vehicles (motorbikes, hatchbacks, sedans): <span className="text-gold-200">₱3,000</span> per applicable claim</li>
                <li>Big vehicles (SUVs, MPVs, pickups, vans): <span className="text-gold-200">₱6,000</span> per applicable claim</li>
              </ul>
              <p className="text-foreground/50 text-xs">
                These fees apply only where supported by the actual insurance policy, your rental agreement, and the circumstances of the claim.
              </p>
            </Section>

            <Section title="Optional Direct Settlement for Eligible Minor Damage">
              <p>
                For eligible minor damage, you may choose direct settlement instead of an insurance claim at
                <span className="text-gold-200"> ₱4,000 per affected panel</span> for direct-settlement labor.
                Paint, materials, and parts are quoted separately where applicable.
              </p>
              <ul className="list-disc list-inside marker:text-gold-500 space-y-1.5">
                <li>A written estimate is provided and your agreement obtained before any work proceeds.</li>
                <li>This charge is never combined with an insurance participation fee for the same damage.</li>
              </ul>
            </Section>

            <Section title="Vehicle Downtime">
              <p>
                If a vehicle needs repair after an incident, applicable daily downtime or rental-extension charges follow
                the rental agreement you sign at pickup and applicable law. The team will explain any downtime charges clearly before they apply.
              </p>
            </Section>

            <Section title="Refundable Security Deposit (separate from insurance)">
              <p>
                The refundable security deposit (₱3,000 motorbikes / ₱5,000 cars) or passport-as-security option is
                <span className="text-gold-200"> separate from insurance</span> and is not an insurance charge.
                Deposits are refunded after vehicle return and inspection, subject to the rental agreement.
              </p>
            </Section>

            <div className="glass-card rounded-2xl p-6 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-foreground/60 leading-relaxed">
                Questions about coverage? Email <a href={`mailto:${CONTACT.email}`} className="text-gold-300 hover:text-gold-100 underline underline-offset-2">{CONTACT.email}</a> or
                message us on <a href={waLink("Hi PB-Lux! I have a question about your insurance and damage policy.")} target="_blank" rel="noreferrer" className="text-green-300 hover:text-green-200 underline underline-offset-2">WhatsApp</a>.
                All terms are subject to the signed rental agreement and the actual insurance policy.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
